import { Button } from '@/shared/components/Button';
import SelectField from '@/shared/components/form/Fields/SelectField';
import Panel from '@/shared/components/Panel';
import { colorBackground, colorText, colorTextAdditional, colorViolet } from '@/utils/palette';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

const GpuOneStorageDisks = ({ storageDisks, isModal = false, isSort = false }) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const sortedDisks = useMemo(() => {
    if (!storageDisks || storageDisks.length === 0) return [];

    return [...storageDisks].sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      if (sortBy === 'size') {
        aValue = aValue.toString();
        bValue = bValue.toString();
      }

      if (sortOrder === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  }, [storageDisks, sortBy, sortOrder]);

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  return (
    <Panel
      lg={isModal ? 6 : 6}
      xl={isModal ? 6 : 6}
      md={isModal ? 6 : 12}
      sm={isModal ? 6 : 12}
      title='Storage Disks'
    >
      <div style={{ display: 'block' }}>
        <Button
          variant='primary'
          onClick={() => {
            setIsFilterOpen(!isFilterOpen);
          }}
          style={{ marginBottom: '10px', float: 'right' }}
        >
          Filter
        </Button>
      </div>
      <br />
      <br />
      {isFilterOpen && (
        <SortContainer>
          <SortSection>
            <SortLabel>Sort by:</SortLabel>
            <SelectField
              style={{ textAlign: 'left !important' }}
              name='sortBy'
              options={[
                { value: 'name', label: 'Name' },
                { value: 'size', label: 'Size' },
                { value: 'model', label: 'Model' },
              ]}
              placeholder='Select Sort By'
              onChange={(e) => handleSortChange(e.value)}
            />
          </SortSection>
          <SortDirectionSection>
            <SortButton
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              aria-label={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
              $isActive={true}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </SortButton>
          </SortDirectionSection>
        </SortContainer>
      )}

      <StorageList>
        {sortedDisks.map((disk, index) => (
          <StorageItem key={index}>
            <StorageHeader>
              <StorageName>{disk.name}</StorageName>
              <StorageSize>{disk.size}</StorageSize>
            </StorageHeader>
            <StorageModel>{disk.model}</StorageModel>
          </StorageItem>
        ))}
      </StorageList>
    </Panel>
  );
};

export default GpuOneStorageDisks;

const StorageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const StorageItem = styled.div`
  padding: 15px;
  background: ${colorBackground};
  border-radius: 8px;
  border-left: 4px solid ${colorViolet};
`;

const StorageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
`;

const StorageName = styled.span`
  font-weight: 600;
  font-size: 17px;
  color: ${colorText};
`;

const StorageSize = styled.span`
  font-size: 15px;
  color: ${colorViolet};
  font-weight: 500;
`;

const StorageModel = styled.p`
  font-size: 15px;
  color: ${colorTextAdditional};
  text-align: left;
`;

const SortContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin: 15px 0;
  padding: 15px;
  background: ${colorBackground};
  border-radius: 8px;
  border: 1px solid ${colorViolet}20;
`;

const SortSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SortDirectionSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SortLabel = styled.span`
  font-size: 14px;
  color: ${colorText};
  font-weight: 600;
  white-space: nowrap;
  text-align: left;
`;

const SortButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${colorViolet}40;
  border-radius: 6px;
  background: ${(props) => (props.$isActive ? colorViolet : 'white')};
  color: ${(props) => (props.$isActive ? 'white' : colorText)};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    // background: ${(props) => (props.$isActive ? colorViolet : colorViolet)}10;
    border-color: ${colorViolet};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: ${colorViolet};
    box-shadow: 0 0 0 2px ${colorViolet}20;
  }

  &:active {
    transform: translateY(0);
  }
`;
